import { Alert, AlertDescription, AlertTitle, Button, Input, Spinner } from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FileDown, Search, X } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { useFiuUtrSearch, useGenerateFiuUtr } from '@/hooks/queries/compliance/use-fiu-utr';
import { getErrorMessage } from '@/lib/api/error';
import { buildGeneratePayload, type FundsTransaction, isNarrativeLengthValid, toClientDetailsPrefill, type UtrConversion } from '@/services/api/fiu-utr';
import { downloadBlob } from '@/utils/download';

// Stable fallback — an inline `?? []` fed to DataTable is the pattern behind a past infinite-render incident
const EMPTY_TRANSACTIONS: FundsTransaction[] = [];

import { CaseDetailsSection } from './case-details-section';
import { DEFAULT_FORM_VALUES, type FiuUtrFormValues, fiuUtrFormSchema } from './case-form';
import { ClientIdentificationSection } from './client-identification-section';
import { ClientIdentityCard } from './client-identity-card';
import { ConversionsCard } from './conversions-card';
import { TransactionsSection } from './transactions-section';

const FETCH_ERROR_TOAST_ID = 'fiu-utr-search-failed';
const GENERATE_ERROR_TOAST_ID = 'fiu-utr-generate-failed';
const XML_MIME_TYPE = 'application/xml';

export const FiuUtrFiling = () => {
  const [pidInput, setPidInput] = useState('');
  const [submittedPid, setSubmittedPid] = useState('');
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<string[]>([]);
  const [conversions, setConversions] = useState<UtrConversion[]>([]);
  const [transactionsWithoutIp, setTransactionsWithoutIp] = useState<string[]>([]);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const query = useFiuUtrSearch(submittedPid);
  const generateMutation = useGenerateFiuUtr();

  const form = useForm<FiuUtrFormValues>({ resolver: zodResolver(fiuUtrFormSchema), mode: 'onChange', defaultValues: DEFAULT_FORM_VALUES });
  const narrative = useWatch({ control: form.control, name: 'narrative' });

  const clientIdentity = query.data?.clientIdentity ?? null;
  const prefilledFromSumsub = useMemo(() => toClientDetailsPrefill(query.data?.clientDetails ?? null).prefilledFromSumsub, [query.data]);

  // Everything below the lookup row renders only once a search has completed
  const hasSearched = query.data !== undefined;
  const isSearching = query.isFetching;

  // biome-ignore lint/correctness/useExhaustiveDependencies: errorUpdatedAt is a deliberate extra trigger — a repeat failure of the same query key never toggles isError, but each failure bumps errorUpdatedAt and must re-toast.
  useEffect(() => {
    // A transactions failure aborts the whole search (fixed in the
    // service): nothing below the lookup row updates.
    if (query.isError) toast.error('Failed to fetch transactions', { id: FETCH_ERROR_TOAST_ID });
  }, [query.isError, query.errorUpdatedAt]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dataUpdatedAt is the trigger — this must run exactly once per COMPLETED search, including re-searches of the same PID, not whenever form helpers or data references change.
  useEffect(() => {
    if (!query.isSuccess || query.isPlaceholderData) return;
    // Every completed search wipes the override inputs (cross-client
    // leakage guard), applies the Sumsub prefill, and clears the selection and any
    // previous conversions. Narrative and IPs persist across searches (legacy).
    const { narrative: currentNarrative, ips } = form.getValues();
    const prefill = toClientDetailsPrefill(query.data.clientDetails);
    form.reset({ ...DEFAULT_FORM_VALUES, narrative: currentNarrative, ips, ...prefill.values });
    setSelectedTransactionIds([]);
    setConversions([]);
    setTransactionsWithoutIp([]);
    setGenerationError(null);
  }, [query.dataUpdatedAt]);

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const pid = pidInput.trim();
    if (!pid) return;
    if (pid === submittedPid) {
      void query.refetch();
      return;
    }
    setSubmittedPid(pid);
  };

  const handleSelectionChange = useCallback((rows: FundsTransaction[]) => {
    setSelectedTransactionIds(rows.map((row) => row.transaction_id));
  }, []);

  // The narrative bounds are also enforced by the field
  // rule and the server zod schema — keep the three in sync.
  const canGenerate = Boolean(clientIdentity) && selectedTransactionIds.length > 0 && isNarrativeLengthValid(narrative);
  const isGenerating = generateMutation.isPending;

  const handleGenerate = async () => {
    if (!canGenerate || isGenerating) return;
    setGenerationError(null);
    // Deliberate fix: clear stale conversions at generate start so a failed
    // regenerate never leaves the previous run's card sitting above the error alert.
    setConversions([]);
    setTransactionsWithoutIp([]);
    const { narrative: caseNarrative, ips, ...clientDetails } = form.getValues();
    const payload = buildGeneratePayload({ pid: submittedPid, narrative: caseNarrative, ips, transactionIds: selectedTransactionIds, clientDetails });
    try {
      const data = await generateMutation.mutateAsync(payload);
      setConversions(data.conversions ?? []);
      setTransactionsWithoutIp(data.transactionsWithoutIp ?? []);
      // Client-side convenience download; the authoritative copy is already in
      // S3, so no retry logic is needed here.
      downloadBlob(data.xml, data.fileName, XML_MIME_TYPE);
      toast.success(`Generated ${data.fileName}`);
    } catch (error) {
      // The upstream message surfaces verbatim — Compliance acts on the
      // validation strings. A 403 here can mean "not a registered FIU reporting
      // person", not a session problem, so no auth-retry is triggered.
      setGenerationError(getErrorMessage(error));
      toast.error('Failed to generate FIU UTR XML', { id: GENERATE_ERROR_TOAST_ID });
    }
  };

  const showSearchError = query.isError && !query.isFetching;

  return (
    <div className="flex w-full flex-col gap-6">
      <h1 className="text-2xl font-semibold">FIU-NL UTR Filing</h1>

      <form className="flex w-full max-w-[640px] items-center gap-3" onSubmit={handleSearchSubmit}>
        <Input
          aria-label="PID (account holder ID)"
          className="flex-1"
          onChange={(event) => setPidInput(event.target.value)}
          placeholder="PID (account holder ID)"
          value={pidInput}
        />
        <Button className="gap-2" disabled={pidInput.trim().length === 0 || isSearching} type="submit">
          {isSearching ? <Spinner className="size-4" /> : <Search className="size-4" />}
          Search
        </Button>
      </form>

      {showSearchError ? <QueryErrorBanner message={`Failed to fetch transactions: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} /> : null}

      {hasSearched ? (
        <div className="flex flex-col gap-8">
          <ClientIdentityCard identity={clientIdentity} />

          <TransactionsSection
            hasMore={query.data?.hasMoreTransactions ?? false}
            isLoading={query.isPending}
            onSelectionChange={handleSelectionChange}
            tableKey={`${submittedPid}:${query.dataUpdatedAt}`}
            transactions={query.data?.transactions ?? EMPTY_TRANSACTIONS}
          />

          <CaseDetailsSection form={form} />

          <ClientIdentificationSection form={form} prefilledFromSumsub={prefilledFromSumsub} />

          <div className="flex flex-col gap-4">
            <ConversionsCard conversions={conversions} />

            {transactionsWithoutIp.length > 0 ? (
              <Alert className="max-w-[900px] border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                <AlertTitle>Filed without an IP address</AlertTitle>
                <AlertDescription className="break-words">
                  No client login was found shortly before {transactionsWithoutIp.join(', ')}. Add IP addresses under Case details and regenerate if these need one.
                </AlertDescription>
              </Alert>
            ) : null}

            {generationError ? (
              <Alert className="relative max-w-[900px] pr-10" variant="destructive">
                <AlertTitle>Failed to generate FIU UTR XML</AlertTitle>
                <AlertDescription className="break-words">{generationError}</AlertDescription>
                <button
                  aria-label="Dismiss error"
                  className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
                  onClick={() => setGenerationError(null)}
                  type="button"
                >
                  <X className="size-4" />
                </button>
              </Alert>
            ) : null}

            <div>
              {/* also disabled while a search is in flight: submittedPid may already point at
                  a new client while the selection still holds the previous client's rows */}
              <Button className="gap-2" disabled={!canGenerate || isGenerating || isSearching} onClick={handleGenerate} size="lg" type="button">
                {isGenerating ? <Spinner className="size-4" /> : <FileDown className="size-4" />}
                Generate &amp; Download XML
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
FiuUtrFiling.displayName = 'FiuUtrFiling';
